import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  AnimatePresence,
  animate,
  motion,
  type MotionValue,
  useDragControls,
  useMotionValue,
  useReducedMotion,
} from "motion/react";
import { cn } from "../ui/utils";
import { AssistantMascot } from "./components/AssistantMascot";
import { ChatPanel } from "./components/ChatPanel";
import { useComplianceAssistantStore } from "./store";
import { useAuthStore } from "@/stores/authStore";
import { useUiContextStore } from "@/stores/uiContextStore";

// Chatbot-scoped CSS injected with the widget; every selector is ca- prefixed and never leaks.
const CA_STYLES = `
.ca-thinking-shimmer{
  background:linear-gradient(90deg,rgb(100 116 139 / .85) 0%,rgb(79 70 229) 45%,rgb(100 116 139 / .85) 90%);
  background-size:200% auto;background-clip:text;-webkit-background-clip:text;color:transparent;
  animation:ca-text-shimmer 2.4s linear infinite;
}
.dark .ca-thinking-shimmer{
  background:linear-gradient(90deg,rgb(159 173 196 / .8) 0%,rgb(196 181 253) 45%,rgb(159 173 196 / .8) 90%);
  background-size:200% auto;background-clip:text;-webkit-background-clip:text;color:transparent;
}
@keyframes ca-text-shimmer{0%{background-position:200% center}100%{background-position:0% center}}
.ca-hover-reveal{opacity:1;transition:opacity .2s ease}
@media (hover:hover) and (pointer:fine){
  .ca-hover-reveal{opacity:0}
  .ca-msg:hover .ca-hover-reveal,.ca-hover-reveal:focus-within{opacity:1}
}
@media (prefers-reduced-motion:reduce){
  .ca-thinking-shimmer{animation:none}
  .ca-hover-reveal{transition:none}
}
`;

const buildGreeting = (name: string) =>
  name
    ? `Hi ${name} 👋, I'm your Compliance Companion. How can I assist you today?`
    : `Hi 👋, I'm your Compliance Companion. How can I assist you today?`;

export function ComplianceAssistant() {
  const open = useComplianceAssistantStore((s) => s.open);
  const minimized = useComplianceAssistantStore((s) => s.minimized);
  const openPanel = useComplianceAssistantStore((s) => s.openPanel);

  const user = useAuthStore((s) => s.user);
  const firstName = (user?.displayName || user?.username || "").trim().split(/\s+/)[0] || "";
  const greeting = buildGreeting(firstName);

  const reduceMotion = useReducedMotion();
  const launcherRef = useRef<HTMLButtonElement>(null);

  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const lastCompactX = useRef(0);
  const lastCompactY = useRef(0);
  const dragControls = useDragControls();
  const [dragConstraints, setDragConstraints] = useState({ top: 0, bottom: 0, left: 0, right: 0 });

  const launcherX = useMotionValue(0);
  const launcherY = useMotionValue(0);
  const launcherDragged = useRef(false);
  const launcherWrapRef = useRef<HTMLDivElement>(null);
  const [launcherConstraints, setLauncherConstraints] = useState({ top: 0, bottom: 0, left: 0, right: 0 });

  const [bubbleOnRight, setBubbleOnRight] = useState(false);
  const [dragging, setDragging] = useState(false);

  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);

  // Hide the floating launcher while a filter drawer (or any other Sheet) is
  // open — it otherwise floats fixed bottom-right, on top of the drawer's
  // own controls.
  // SheetContent publishes its own open/closed state to the store, so this is a plain subscription --
  // no DOM query, no MutationObserver.
  const sheetOpen = useUiContextStore((s) => s.openSheetCount > 0);

  useEffect(() => {
    if (open) return;
    const clamp = (v: number, min: number, max: number) => Math.max(min, Math.min(max, v));
    const recompute = () => {
      const el = launcherWrapRef.current;
      if (!el) return;
      // offsetWidth/offsetHeight ignore the drag transform, so the constraints stay stable.
      const w = el.offsetWidth;
      const h = el.offsetHeight;
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const RIGHT = vw >= 640 ? 24 : 20;
      const BOTTOM = 96;
      const M = 8;
      const left = -Math.max(0, vw - RIGHT - w - M);
      const right = Math.max(0, RIGHT - M);
      const top = -Math.max(0, vh - BOTTOM - h - M);
      const bottom = Math.max(0, BOTTOM - M);
      setLauncherConstraints({ top, bottom, left, right });
      launcherX.set(clamp(launcherX.get(), left, right));
      launcherY.set(clamp(launcherY.get(), top, bottom));
    };
    recompute();
    window.addEventListener("resize", recompute);
    return () => window.removeEventListener("resize", recompute);
  }, [open, mounted, launcherX, launcherY]);

  const wasOpen = useRef(open);
  useEffect(() => {
    if (wasOpen.current && !open) launcherRef.current?.focus();
    wasOpen.current = open;
  }, [open]);

  useEffect(() => {
    if (open) {
      x.set(0);
      y.set(0);
      lastCompactX.current = 0;
      lastCompactY.current = 0;
      setBubbleOnRight(false);
      if (reduceMotion) {
        launcherX.set(0);
        launcherY.set(0);
      } else {
        animate(launcherX, 0, { type: "spring", stiffness: 420, damping: 34 });
        animate(launcherY, 0, { type: "spring", stiffness: 420, damping: 34 });
      }
    }
  }, [open, x, y, launcherX, launcherY, reduceMotion]);

  useEffect(() => {
    const settle = (mv: MotionValue<number>, target: number) => {
      if (target === mv.get()) return;
      if (reduceMotion) mv.set(target);
      else animate(mv, target, { type: "spring", stiffness: 500, damping: 44 });
    };
    const recompute = () => {
      if (!minimized) return;
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const MARGIN = 8;
      const SIDE_GAP = 16;
      const width = Math.min(400, vw - 32);
      const height = Math.min(vh * 0.6, 500);
      const top = Math.min(0, MARGIN - (vh - SIDE_GAP - height));
      const left = Math.min(0, MARGIN - (vw - SIDE_GAP - width));
      setDragConstraints({ top, bottom: 0, left, right: 0 });
      settle(x, Math.max(left, Math.min(0, lastCompactX.current)));
      settle(y, Math.max(top, Math.min(0, lastCompactY.current)));
    };
    recompute();
    window.addEventListener("resize", recompute);
    return () => window.removeEventListener("resize", recompute);
  }, [minimized, reduceMotion, x, y]);

  if (!mounted || typeof document === "undefined") return null;

  const sizeClass = "h-[min(500px,60dvh)] w-[min(400px,calc(100vw-2rem))]";

  return createPortal(
    <>
      <style>{CA_STYLES}</style>
      <AnimatePresence>
        {!open && !sheetOpen && (
          <motion.div
            key="ca-launcher"
            ref={launcherWrapRef}
            drag
            dragMomentum={false}
            dragElastic={0.08}
            dragConstraints={launcherConstraints}
            style={{ x: launcherX, y: launcherY }}
            onPointerDownCapture={() => {
              launcherDragged.current = false;
            }}
            onDragStart={() => {
              launcherDragged.current = true;
              setDragging(true);
              document.body.style.cursor = "grabbing";
            }}
            onDragEnd={() => {
              setDragging(false);
              document.body.style.cursor = "";
              const el = launcherWrapRef.current;
              if (el) {
                const rect = el.getBoundingClientRect();
                setBubbleOnRight(rect.left + rect.width / 2 < window.innerWidth / 2);
              }
            }}
            initial={{ opacity: 0, scale: 0.6 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.6 }}
            transition={{ type: "spring", stiffness: 320, damping: 22 }}
            className="group fixed bottom-24 right-5 z-[60] cursor-grab touch-none active:cursor-grabbing sm:right-6"
          >
            <div
              aria-hidden
              className={cn(
                "pointer-events-none absolute top-1/2 w-max max-w-[15rem] -translate-y-1/2",
                "rounded-2xl border border-border bg-popover/95 px-3.5 py-2.5 backdrop-blur-sm",
                "text-[12.5px] font-medium leading-snug text-popover-foreground",
                "scale-95 opacity-0 shadow-xl shadow-violet-500/15",
                "transition-all duration-300 ease-out",
                bubbleOnRight
                  ? "left-full ml-4 origin-left -translate-x-2 rounded-bl-md"
                  : "right-full mr-4 origin-right translate-x-2 rounded-br-md",
                // Hover-reveal ONLY; do NOT re-add focus-within - it popped the greeting on every panel close.
                !dragging && "group-hover:translate-x-0 group-hover:scale-100 group-hover:opacity-100",
              )}
            >
              {greeting}
              <span
                className={cn(
                  "absolute top-1/2 size-2.5 -translate-y-1/2 rotate-45 rounded-[2px] border-border bg-popover/95",
                  bubbleOnRight ? "left-[-5px] border-b border-l" : "right-[-5px] border-b border-r",
                )}
              />
            </div>
            <motion.button
              ref={launcherRef}
              type="button"
              onClick={() => {
                // A drag ends with a click too; swallow it so dragging the bubble does not open the chat.
                if (launcherDragged.current) {
                  launcherDragged.current = false;
                  return;
                }
                openPanel();
              }}
              aria-label="Open Compliance Companion"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.92 }}
              transition={{ type: "spring", stiffness: 400, damping: 16 }}
              className="relative block rounded-2xl outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
            >
              <motion.span
                aria-hidden
                className="pointer-events-none absolute -inset-2 rounded-full bg-[radial-gradient(circle,rgba(139,92,246,0.5),transparent_70%)] blur-xl"
                animate={reduceMotion ? undefined : { opacity: [0.4, 0.7, 0.4], scale: [0.92, 1.08, 0.92] }}
                transition={
                  reduceMotion ? undefined : { duration: 3.6, repeat: Infinity, ease: "easeInOut" }
                }
              />
              <span className="pointer-events-none absolute -inset-2 rounded-full bg-violet-500/30 opacity-0 blur-2xl transition-opacity duration-300 group-hover:opacity-100" />
              <span
                aria-hidden
                className="relative block drop-shadow-[0_8px_18px_rgba(124,58,237,0.5)] transition-[filter] duration-300 group-hover:drop-shadow-[0_14px_30px_rgba(139,92,246,0.72)]"
              >
                <AssistantMascot className="size-14 sm:size-16" />
              </span>
            </motion.button>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {open && minimized && (
          <motion.aside
            key="ca-panel"
            drag={minimized}
            dragListener={false}
            dragControls={dragControls}
            dragConstraints={dragConstraints}
            dragMomentum={false}
            dragElastic={0.06}
            onDragStart={() => {
              document.body.style.cursor = "grabbing";
            }}
            onDragEnd={() => {
              document.body.style.cursor = "";
              lastCompactX.current = x.get();
              lastCompactY.current = y.get();
            }}
            style={{ x, y }}
            initial={{ opacity: 0, scale: 0.94 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.94 }}
            whileDrag={reduceMotion ? undefined : { scale: 1.02 }}
            transition={{ type: "tween", ease: [0.4, 0, 0.2, 1], duration: 0.28 }}
            className={cn(
              "ca-panel-glow fixed bottom-4 right-4 z-[60] flex origin-bottom-right flex-col overflow-hidden rounded-2xl border border-border transition-[width,height] duration-300 ease-[cubic-bezier(0.4,0,0.2,1)] dark:border-violet-400/40",
              sizeClass,
            )}
            role="dialog"
            aria-label="Compliance Companion"
          >
            <ChatPanel dragControls={dragControls} />
          </motion.aside>
        )}
      </AnimatePresence>
    </>,
    document.body,
  );
}
